import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog"
import { Button } from "./ui/button"
import { Progress } from "./ui/progress"
import { Alert, AlertDescription } from "./ui/alert"
import { Card, CardContent } from "./ui/card"
import { Separator } from "./ui/separator"
import fileService from '../services/fileService';
import uploadSettingsService from '../services/uploadSettingsService';
import config from '../config';
import { formatFileSize, isValidFileSize } from '../utils/helpers';
import {
  Cloud,
  UploadCloud,
  Trash2,
  AlertTriangle,
  File,
  FileText,
  Image as ImageIcon,
  Music,
  Video,
  X,
  Loader2
} from 'lucide-react';
import { cn } from "../lib/utils"

const FileUpload = ({ open, onClose, onUploadSuccess, folderId, droppedFiles }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [storageInfo, setStorageInfo] = useState(null);
  const [loadingStorage, setLoadingStorage] = useState(true);
  // Dynamic max file size — loaded from admin upload settings
  const [maxFileSize, setMaxFileSize] = useState(config.MAX_FILE_SIZE);

  useEffect(() => {
    if (open) {
      loadStorageInfo();
      loadUploadSettings();
      if (droppedFiles && droppedFiles.length > 0) {
        processDroppedFiles(droppedFiles);
      }
    }
  }, [open, droppedFiles]);

  const loadStorageInfo = async () => {
    try {
      setLoadingStorage(true);
      const data = await fileService.getStorageStats();
      setStorageInfo(data.storage);
    } catch (err) {
      console.error('Error loading storage info:', err);
    } finally {
      setLoadingStorage(false);
    }
  };

  const loadUploadSettings = async () => {
    try {
      const data = await uploadSettingsService.getPublicSettings();
      if (data?.max_file_size_mb) {
        // API returns MB, convert to bytes
        setMaxFileSize(data.max_file_size_mb * 1024 * 1024);
      }
    } catch (err) {
      console.error('Error loading upload settings, using default:', err);
    }
  };



  /* Refactored upload logic to support both auto-upload and manual retry */

  const processDroppedFiles = (files) => {
    setError('');

    if (files.length === 0) return;

    if (files.length > 10) {
      setError('Maksimal 10 file per upload');
      return;
    }

    const invalidFiles = files.filter(file => !isValidFileSize(file, maxFileSize));
    if (invalidFiles.length > 0) {
      setError(`Beberapa file melebihi ${formatFileSize(maxFileSize)}`);
      return;
    }

    const totalSize = files.reduce((sum, file) => sum + file.size, 0);

    if (storageInfo) {
      const { used, limit, available } = storageInfo;
      if (totalSize > available) {
        setError(`Storage limit exceeded! File terlalu besar.`);
        return;
      }
    }

    setSelectedFiles(files);
    // User must click "Unggah" button to start upload
  };

  const executeUpload = async (filesToUpload) => {
    if (!filesToUpload || filesToUpload.length === 0) {
      setError('Harap pilih file');
      return;
    }

    try {
      setUploading(true);
      setError('');

      await fileService.uploadFiles(
        filesToUpload,
        folderId,
        (progress) => setUploadProgress(progress)
      );

      setSelectedFiles([]);
      setUploadProgress(0);
      onUploadSuccess();
      onClose();
    } catch (err) {
      console.error('Upload error:', err);
      if (err.response?.status === 400 && err.response?.data?.storageInfo) {
        setError('Storage limit exceeded! Storage penuh.');
      } else {
        setError(err.response?.data?.message || 'Gagal mengunggah file');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleUploadClick = () => {
    executeUpload(selectedFiles);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);

    setError('');

    if (files.length === 0) return;

    if (files.length > 10) {
      setError('Maksimal 10 file per upload');
      return;
    }

    const invalidFiles = files.filter(file => !isValidFileSize(file, maxFileSize));
    if (invalidFiles.length > 0) {
      setError(`Beberapa file melebihi ${formatFileSize(maxFileSize)}`);
      return;
    }

    const totalSize = files.reduce((sum, file) => sum + file.size, 0);

    if (storageInfo) {
      const { used, limit, available } = storageInfo;
      if (totalSize > available) {
        setError(`Storage limit exceeded! File terlalu besar.`);
        return;
      }
    }

    setSelectedFiles(files);
    // User must click "Unggah" button to start upload

    // Reset input so same file can be re-selected
    event.target.value = null;
  };

  const handleRemoveFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    setError('');
  };

  const handleClearAll = () => {
    setSelectedFiles([]);
    setError('');
  };

  const handleClose = () => {
    if (!uploading) {
      setSelectedFiles([]);
      setUploadProgress(0);
      setError('');
      onClose();
    }
  };

  const getStoragePercentage = () => {
    if (!storageInfo) return 0;
    return (storageInfo.used / storageInfo.limit) * 100;
  };

  const getTotalSize = () => {
    return selectedFiles.reduce((sum, file) => sum + file.size, 0);
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return <File className="h-4 w-4" />;
    if (mimeType.includes('pdf')) return <FileText className="h-4 w-4 text-red-500" />;
    if (mimeType.startsWith('image')) return <ImageIcon className="h-4 w-4 text-green-500" />;
    if (mimeType.includes('video')) return <Video className="h-4 w-4 text-orange-500" />;
    if (mimeType.includes('audio')) return <Music className="h-4 w-4 text-purple-500" />;
    return <File className="h-4 w-4 text-blue-500" />;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5 text-primary" />
            Unggah File
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Storage Info */}
          {!loadingStorage && storageInfo && (
            <Card className={cn(
              "border-2",
              getStoragePercentage() > 80 ? "border-yellow-400 bg-yellow-50" : "border-gray-200 bg-gray-50"
            )}>
              <CardContent className="p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold">Penyimpanan Anda</span>
                  <span className="text-sm font-bold">{getStoragePercentage().toFixed(1)}%</span>
                </div>
                <Progress value={getStoragePercentage()} className="h-2 mb-2" />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{formatFileSize(storageInfo.used)} / {formatFileSize(storageInfo.limit)}</span>
                  <span>Tersedia: {formatFileSize(storageInfo.available)}</span>
                </div>
                {getStoragePercentage() > 80 && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-yellow-700 font-medium">
                    <AlertTriangle className="h-3 w-3" />
                    Penyimpanan hampir penuh!
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Upload Area */}
          <div className="grid w-full items-center gap-1.5">
            <input
              id="file-upload"
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
              disabled={uploading}
            />
            {/* Removed label wrapper to prevent double-click issue */}
            <Button
              variant="outline"
              className="w-full h-20 border-dashed border-2 flex flex-col gap-2"
              disabled={uploading}
              // Removed asChild since we are not wrapping a custom component that needs it
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation(); // Just to be safe
                document.getElementById('file-upload').click();
              }}
            >
              <div className="cursor-pointer flex flex-col items-center">
                <UploadCloud className="h-6 w-6 text-muted-foreground" />
                <span>Klik untuk memilih file (max 10)</span>
              </div>
            </Button>
          </div>

          {/* File List */}
          {selectedFiles.length > 0 && (
            <Card className="border-gray-200 bg-blue-50/50">
              <CardContent className="p-0">
                <div className="p-3 border-b flex justify-between items-center">
                  <span className="text-sm font-semibold">File Terpilih ({selectedFiles.length})</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={handleClearAll}
                    disabled={uploading}
                  >
                    Hapus Semua
                  </Button>
                </div>
                <div className="max-h-[160px] overflow-y-auto p-2 space-y-1">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 rounded hover:bg-white text-sm group">
                      <div className="flex items-center gap-2 overflow-hidden">
                        {getFileIcon(file.type)}
                        <span className="truncate max-w-[200px]">{file.name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">{formatFileSize(file.size)}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500"
                          onClick={() => handleRemoveFile(index)}
                          disabled={uploading}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <Separator />
                <div className="p-2 bg-gray-50 text-xs text-center text-muted-foreground rounded-b-lg">
                  Total Ukuran: {formatFileSize(getTotalSize())}
                </div>
              </CardContent>
            </Card>
          )}

          {uploading && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span>Mengunggah...</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={handleClose} disabled={uploading}>
            Batal
          </Button>
          <Button
            onClick={handleUploadClick}
            disabled={selectedFiles.length === 0 || uploading}
          >
            {uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Mengunggah...
              </>
            ) : (
              `Unggah (${selectedFiles.length})`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FileUpload;